import React, { useState, useEffect } from 'react';
import { Info, AlertTriangle, CheckCircle, XCircle, X, Globe } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const colorStyles = {
  green: {
    bg: 'bg-green-50 border-green-200',
    text: 'text-green-800',
    icon: <CheckCircle className="w-5 h-5 text-green-600" />,
    closeBtn: 'text-green-600 hover:text-green-800 hover:bg-green-100'
  },
  blue: {
    bg: 'bg-blue-50 border-blue-200',
    text: 'text-blue-800',
    icon: <Info className="w-5 h-5 text-blue-600" />,
    closeBtn: 'text-blue-600 hover:text-blue-800 hover:bg-blue-100'
  },
  yellow: {
    bg: 'bg-yellow-50 border-yellow-200',
    text: 'text-yellow-800',
    icon: <AlertTriangle className="w-5 h-5 text-yellow-600" />,
    closeBtn: 'text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100'
  },
  red: {
    bg: 'bg-red-50 border-red-200',
    text: 'text-red-800',
    icon: <XCircle className="w-5 h-5 text-red-600" />,
    closeBtn: 'text-red-600 hover:text-red-800 hover:bg-red-100'
  }
};

const BannerRow = ({ data, isGlobal, onDismiss }) => {
  const style = colorStyles[data.color] || colorStyles.blue;
  return (
    <div className={`${style.bg} border-b px-3 py-2 sm:px-5`} data-testid={isGlobal ? 'global-announcement-banner' : 'local-announcement-banner'}>
      <div className="mx-auto grid max-w-5xl grid-cols-[1.5rem_minmax(0,1fr)_1.5rem] items-start gap-2 sm:gap-3">
        <span className={`${style.text} mt-0.5 flex justify-center`} aria-hidden="true">
          {isGlobal ? <Globe className="h-5 w-5 opacity-80" /> : style.icon}
        </span>
        <p className={`${style.text} min-w-0 whitespace-normal break-words text-center text-sm font-medium leading-5 [overflow-wrap:anywhere]`}>
          {isGlobal && <span className="mr-2 text-xs uppercase tracking-wide opacity-70">Global</span>}
          {data.message}
        </p>
        <button
          onClick={onDismiss}
          className={`${style.closeBtn} -m-1 mt-[-0.125rem] flex h-7 w-7 items-center justify-center rounded-full transition-colors`}
          title="Schließen"
          aria-label="Banner schließen"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const AnnouncementBanner = ({ viewerRole = 'employee' }) => {
  const [announcement, setAnnouncement] = useState(null);
  const [globalAnnouncement, setGlobalAnnouncement] = useState(null);
  const [dismissedLocalKey, setDismissedLocalKey] = useState(null);
  const [dismissedGlobalKey, setDismissedGlobalKey] = useState(null);

  useEffect(() => {
    fetchBoth();
    const interval = setInterval(fetchBoth, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchBoth = async () => {
    try {
      const [local, global] = await Promise.allSettled([
        axios.get(`${API}/api/announcement`),
        axios.get(`${API}/api/global-announcement`),
      ]);
      if (local.status === 'fulfilled') setAnnouncement(local.value.data);
      if (global.status === 'fulfilled') setGlobalAnnouncement(global.value.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const localVisible = announcement && announcement.is_active && announcement.message
    && dismissedLocalKey !== (announcement.last_updated || 'local');

  // Global banner target filter: "admin" | "employee" | "both" (default "both" for backward compat)
  const globalTarget = (globalAnnouncement && globalAnnouncement.target) || 'both';
  const globalTargetMatches = globalTarget === 'both' || globalTarget === viewerRole;
  const globalVisible = globalAnnouncement && globalAnnouncement.is_active && globalAnnouncement.message
    && globalTargetMatches
    && dismissedGlobalKey !== (globalAnnouncement.last_updated || 'global');

  if (!localVisible && !globalVisible) return null;

  return (
    <>
      {globalVisible && (
        <BannerRow
          data={globalAnnouncement}
          isGlobal
          onDismiss={() => setDismissedGlobalKey(globalAnnouncement.last_updated || 'global')}
        />
      )}
      {localVisible && (
        <BannerRow
          data={announcement}
          isGlobal={false}
          onDismiss={() => setDismissedLocalKey(announcement.last_updated || 'local')}
        />
      )}
    </>
  );
};

export default AnnouncementBanner;
